import { useEffect, useMemo, useState } from "react";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import { buildPortOptions } from "@features/vessel-call/create/utils/portOptions.ts";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  clearCertificates,
  FORM_FIELDS,
  getLatestApprovedCertificatesToClear,
} from "@features/vessel-register/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";

const currentYear = new Date().getFullYear();
const shipBuildYears = Array.from({ length: currentYear - 1800 + 1 }, (_, i) => {
  const year = currentYear - i;
  return { label: year.toString(), value: year };
});

const VesselInformation = () => {
  const {
    formData,
    updateFormData,
    watch,
    isRestrictedToCertificatesUpdate,
    vesselDetails: { latestApprovedCertificates } = {},
  } = useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();

  const [flagPortWarningMsg, setFlagPortWarningMsg] = useState<string | null>(null);

  const { options: countries = [] } = useFormattedReference<{
    countryCode: string;
    countryName?: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-vessel");

  const { options: fetchedPorts = [] } = useFormattedReference<{
    portCode: string;
    portName?: string;
    countryCode?: string;
  }>(REFERENCE_DATA_TYPES.PORTS, "bff-vessel");

  const selectedPortCode = watch(FORM_FIELDS.PORT_OF_REGISTRATION);
  const selectedFlag = watch(FORM_FIELDS.FLAG);
  const noImoNumber = watch(FORM_FIELDS.NO_IMO_NUMBER);
  const yearBuilt = watch(FORM_FIELDS.YEAR_BUILT);

  const minDeliveryDate = useMemo(() => {
    if (!yearBuilt) return undefined;
    return new Date(yearBuilt, 0, 1);
  }, [yearBuilt]);

  useEffect(() => {
    if (selectedFlag && selectedPortCode) {
      const selectedPort = fetchedPorts.find((port) => port.portCode === selectedPortCode);

      if (selectedPort && selectedPort.countryCode !== selectedFlag) {
        setFlagPortWarningMsg(t("form-validation.flag-port-mismatch"));
      } else {
        setFlagPortWarningMsg(null);
      }
    } else {
      setFlagPortWarningMsg(null);
    }
  }, [fetchedPorts, selectedFlag, selectedPortCode]);

  const portOptions = useMemo(() => buildPortOptions(fetchedPorts), [fetchedPorts]);

  const selectedPortOption = useMemo(() => {
    const selectedPort = fetchedPorts.find((port) => port.portCode === selectedPortCode);

    return selectedPort
      ? {
          label: selectedPort.portName
            ? `${selectedPort.portName} (${selectedPort.portCode})`
            : String(selectedPort.portCode),
          value: String(selectedPort.portCode),
        }
      : null;
  }, [fetchedPorts, selectedPortCode]);

  const countriesOptions = useMemo(
    () =>
      countries.map((country) => ({
        label: `${country.countryCode} - ${country.countryName}`,
        value: country.countryCode,
      })),
    [countries],
  );

  const clearCertificatesAndNotify = () => {
    if (latestApprovedCertificates && latestApprovedCertificates.length > 0) {
      const currentCertificates = formData.getValues(FORM_FIELDS.CERTIFICATES);
      const certsToClear = getLatestApprovedCertificatesToClear(
        currentCertificates,
        latestApprovedCertificates,
      );

      if (!certsToClear.length) return;

      notify({
        type: "warning",
        title: t("vessel-management.notifications.this-will-invalidate-certificates"),
      });
      clearCertificates(formData.setValue, certsToClear, updateFormData);
    }
  };

  return (
    <div data-testid="vessel-information-page" className="pb-8">
      <FormProvider {...formData}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-1 lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.IMO>
              testId="register-imo-number-input"
              name={FORM_FIELDS.IMO}
              type="number"
              slotProps={{
                htmlInput: {
                  min: "1",
                },
              }}
              label={t("vessel-management.imo-number")}
              onFieldChange={(key, value) => {
                updateFormData(key, value);
                formData.trigger(FORM_FIELDS.IMO);
              }}
              maxDigits={7}
              disabled={isRestrictedToCertificatesUpdate || !!noImoNumber}
            />
          </div>
          <div className="lg:col-span-2 lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NO_IMO_NUMBER>
              name={FORM_FIELDS.NO_IMO_NUMBER}
              type="checkbox"
              label={t("vessel-management.no-imo-number")}
              testId="no-imo-number"
              onFieldChange={(key, value) => {
                updateFormData(key, value);
                if (value) {
                  formData.clearErrors(FORM_FIELDS.IMO);
                  formData.trigger(FORM_FIELDS.OFFICIAL_NUMBER);
                } else {
                  formData.clearErrors(FORM_FIELDS.OFFICIAL_NUMBER);
                  formData.trigger(FORM_FIELDS.IMO);
                }
              }}
              defaultValue={noImoNumber}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-span-1 lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.OFFICIAL_NUMBER>
              testId="register-official-number-input"
              name={FORM_FIELDS.OFFICIAL_NUMBER}
              type="text"
              maxChars={15}
              label={t("vessel-management.official-number")}
              onFieldChange={(key, value) => {
                updateFormData(key, value);
                formData.trigger(FORM_FIELDS.OFFICIAL_NUMBER);
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.VESSEL_NAME>
              testId="register-vessel-name-input"
              name={FORM_FIELDS.VESSEL_NAME}
              type="text"
              maxChars={50}
              label={t("vessel-management.vessel-name")}
              onFieldChange={(k, v) => {
                clearCertificatesAndNotify();
                updateFormData(k, v);
              }}
              enforceUppercase
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-2">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.CALL_SIGN>
              testId="register-call-sign-input"
              name={FORM_FIELDS.CALL_SIGN}
              type="text"
              maxChars={15}
              label={t("vessel-management.call-sign")}
              onFieldChange={updateFormData}
              enforceUppercase
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.PORT_OF_REGISTRATION>
              testId="register-port-of-registration-autocomplete"
              name={FORM_FIELDS.PORT_OF_REGISTRATION}
              type="autocomplete"
              options={portOptions}
              autocompleteValue={selectedPortOption}
              label={t("vessel-management.port-of-registration")}
              onFieldChange={(k, v) => {
                clearCertificatesAndNotify();
                updateFormData(k, v);
              }}
              warningMsg={flagPortWarningMsg}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-2">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.FLAG>
              testId="register-flag-autocomplete"
              name={FORM_FIELDS.FLAG}
              type="autocomplete"
              options={countriesOptions}
              label={t("vessel-management.flag")}
              onFieldChange={(k, v) => {
                clearCertificatesAndNotify();
                updateFormData(k, v);
              }}
              warningMsg={flagPortWarningMsg}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.YEAR_BUILT>
              testId="register-ship-build-year-autocomplete"
              name={FORM_FIELDS.YEAR_BUILT}
              type="autocomplete"
              options={shipBuildYears}
              label={t("vessel-management.ship-build-year")}
              onFieldChange={updateFormData}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="lg:col-start-2">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DATE_OF_DELIVERY>
              testId="register-date-of-delivery-picker"
              name={FORM_FIELDS.DATE_OF_DELIVERY}
              type="date-picker"
              options={shipBuildYears}
              label={t("vessel-management.date-of-delivery")}
              onFieldChange={updateFormData}
              timePicker={false}
              dateConstraint="not-future"
              minDate={minDeliveryDate}
              maxDate={new Date()}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default VesselInformation;
